/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
/* global gettext */
import React from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import { List } from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';

import Loading from 'components/Loading';
import ScrollableTableContainer from 'components/ScrollableTableContainer';
import SortIcon from 'components/table/SortIcon';
import PageSizeSelector from 'components/table/PageSizeSelector';
import SearchForm from 'components/form/SearchForm';
import Table from 'components/Table';
import ListItem from './ListItem';
import ListPagination from './ListPagination';


const ListHeader = ({ header, setSortBy, sortBy, reversed }) => {
  const { displayName, sortable, name } = header.toObject();

  return (
    <th>
      {displayName}
      {sortable && (
        <SortIcon sortName={name.join('__')} sortBy={sortBy} setSortBy={setSortBy} reversed={reversed} />
      )}
    </th>
  );
};

ListHeader.propTypes = {
  sortBy: PropTypes.string,
  reversed: PropTypes.bool,
  setSortBy: PropTypes.func.isRequired,
  header: ImmutablePropTypes.map.isRequired,
};
ListHeader.defaultProps = {
  sortBy: null,
  reversed: false,
};

const PAGE_SIZES = [10, 25, 50, 100];

const ListItems = ({
  title, collection, items, noItemsMessage, fields,
  listName, noSearch, sortBy, setSortBy, sortByReversed,
  hidePagination, pageSize, isLoading,
  removeItem, search, loadPage, setPageSize,
}) => (
  <div className="list-container">
    <div className="clearfix mb-1 mt-1">
      {title && (
        <div className="col-1-2">
          <h3>{title}</h3>
        </div>
      )}
      <div className={title ? 'col-1-2' : 'col-1'}>
        {!noSearch && collection && (
          <SearchForm
            onSubmit={({ searchString }) => search(searchString)}
            form={`${listName}_search`}
            isLoading={isLoading}
            initialValues={{ searchString: collection.get('searchString') }}
          />
        )}
      </div>
      <div>
        {gettext('Show')} <PageSizeSelector
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageSizeOptions={PAGE_SIZES}
        /> {gettext('entries')}
      </div>
    </div>

    {!collection ? (
      <Loading />
    ) : (
      <ScrollableTableContainer>
        {isLoading && (
          <div className="listing-loader" >
            <Loading />
          </div>
        )}

        <Table
          className="padded"
          renderRow={(item, columns) => (
            <ListItem
              key={item}
              item={item}
              fields={columns}
              removeItem={removeItem}
            />
          )}
          renderHeaders={headers => headers.map(header => (
            <ListHeader
              key={header}
              header={header}
              sortBy={sortBy}
              setSortBy={setSortBy}
              reversed={sortByReversed}
            />
          ))}
          rows={collection ? collection.get('items', List()) : items}
          columns={fields}
          emptyMessage={noItemsMessage || gettext('None Found.')}
        />
      </ScrollableTableContainer>
    )}

    {!hidePagination && collection && (
      <ListPagination collection={collection} pageSize={pageSize} loadPage={loadPage} />
    )}
  </div>
);

ListItems.propTypes = {
  title: PropTypes.string,
  collection: ImmutablePropTypes.map, // Must provide one of these
  items: ImmutablePropTypes.list,
  noItemsMessage: PropTypes.string,
  fields: ImmutablePropTypes.list.isRequired,
  listName: PropTypes.string,
  noSearch: PropTypes.bool,
  hidePagination: PropTypes.bool,
  pageSize: PropTypes.number,
  isLoading: PropTypes.bool,
  removeItem: PropTypes.func,
  setSortBy: PropTypes.func,
  search: PropTypes.func,
  loadPage: PropTypes.func,
  sortBy: PropTypes.string,
  sortByReversed: PropTypes.bool,
  setPageSize: PropTypes.func.isRequired,
};
ListItems.defaultProps = {
  title: null,
  collection: null,
  items: null,
  noItemsMessage: null,
  listName: null,
  noSearch: null,
  hidePagination: null,
  pageSize: null,
  isLoading: null,
  removeItem: null,
  setSortBy: null,
  search: null,
  loadPage: null,
  sortBy: null,
  sortByReversed: null,
};

export default ListItems;
